<template>
  <div
    class="mt-6"
  >
    <div
      v-if="isPageLoading"
      class="loader mx-auto h-100"
    />
    <template
      v-else
    >
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <h1 class="page-title">
              Daily delivery
            </h1>
          </b-col>
          <b-col class="text-right">
            <b-btn
              variant="primary"
              class="ml-2 border-0 justify-content-right"
              data-test="export-delivery-data-csv-button"
              @click="downloadAsCSV()"
              :disabled="!hasDeliveryData || !allParametersArePresent"
            >
              Export all delivery data as CSV
            </b-btn>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row
          align-h="between"
          class="mb-3"
        >
          <b-col
            cols="auto"
          >
            <base-filter-dropdown
              :filter="'publisher'"
              :options-list="campaignPublishersList"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedPublishersIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedPublishersIds', ...arguments)"
              @selectAllOptions="selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedPublishersIds')"
            />
            <base-filter-dropdown
              class="ml-3"
              :filter="'creative'"
              :options-list="GET_ACTIVE_CREATIVES"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedCreativesIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedCreativesIds', ...arguments)"
              @selectAllOptions="selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedCreativesIds')"
            />
            <base-filter-dropdown
              class="ml-3"
              :filter="'content items'"
              :options-list="campaignContentItems"
              :key-property="'encodedExternalId'"
              :value-property="'title'"
              :selected-options="selectedContentItemIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedContentItemIds', ...arguments)"
              @selectAllOptions="selectAllOptions('campaignContentItems', 'selectedContentItemIds', 'encodedExternalId')"
              @deselectAllOptions="deselectAllOptions('selectedContentItemIds')"
            />
          </b-col>
          <b-col
            cols="auto"
          >
            <date-picker-dropdown
              @updateDates="updateDates"
              right
              toggle-class="text-right"
            />
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row>
          <b-col
            :cols="12"
          >
            <b-card
              no-body
            >
              <b-card-body
                class="pb-0"
                style="min-height: calc(28rem);"
              >
                <div
                  v-if="!deliveryChartData.loaded"
                  class="loader mx-auto my-5"
                />
                <DeliveryChart
                  v-else
                  :data="deliveryChartData"
                />
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row>
          <b-col
            cols="12"
          >
            <b-card
              class="mb-5"
            >
              <div class="table-responsive">
                <b-table
                  ref="delivery-report-table"
                  class="card-table table-vcenter text-nowrap delivery-report-table"
                  tbody-tr-class="campaign-table-row"
                  :striped="true"
                  :hover="true"
                  :items="dailyStats"
                  :fields="returnHeaders"
                  :busy.sync="isDeliveryTableBusy"
                  show-empty
                  data-test="delivery-report-table"
                  sort-by="date"
                >
                  <template #table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.key === 'show_details' ? '5%' : 'initial' }"
                    >
                  </template>
                  <template
                    #table-busy
                    style="min-height: calc(16rem);"
                  >
                    <div class="loader mx-auto" />
                  </template>
                  <template #cell(show_details)="row">
                    <p
                      ref="icon-column"
                      class="mb-0 pl-3"
                    >
                      <i
                        class="far"
                        :data-test="`toggle-details-${row.item.date}`"
                        :class="row.detailsShowing ? 'fa-minus-square' : 'fa-plus-square'"
                        @click="row.toggleDetails"
                      />
                    </p>
                  </template>
                  <template
                    #cell(date)="{item}"
                  >
                    <p ref="date-column">
                      {{ formatDateForEoC(item.date) }}
                    </p>
                  </template>
                  <template #cell(impressions)="{item}">
                    <p ref="impressions-column">
                      {{ toLocaleString(item.impressions) }}
                    </p>
                  </template>
                  <template #cell(clicks)="{item}">
                    <p ref="clicks-column">
                      {{ toLocaleString(item.clicks) }}
                    </p>
                  </template>
                  <template #cell(ctr)="{item}">
                    <p ref="ctr-column">
                      {{ toLocaleString(item.ctr, true) }}%
                    </p>
                  </template>
                  <template #cell(scrolls)="{item}">
                    <p ref="scrolls-column">
                      {{ isValidNumber(item.scrolls) ? toLocaleString(item.scrolls) : "N/A" }}
                    </p>
                  </template>
                  <template #cell(str)="{item}">
                    <p ref="str-column">
                      {{ isValidNumber(item.str) ? toLocaleString(item.str, true) + "%" : "N/A" }}
                    </p>
                  </template>
                  <template #cell(cost)="{item}">
                    <p ref="cost-column">
                      {{ formattedMoneyAmount(item.cost) }}
                    </p>
                  </template>
                  <template #row-details="{item}">
                    <div
                      class="my-2 bg-white"
                      v-for="(creative, index) in item.creativeStats"
                      :key="index"
                      :data-test="`${creative.creativeTemplateName}`"
                    >
                      <div
                        class="custom-row-details-wrapper bg-light"
                      >
                        <div>
                          <td
                            ref="row-details-icon"
                            class="row-details-icon"
                          >
                            <!-- Empty for Styling -->
                          </td>
                          <td
                            class="text-truncate row-details-size-name"
                            data-test="creative-template-name"
                            ref="row-details-size-name"
                          >
                            {{ creative.sizeName }}
                          </td>
                          <td
                            ref="row-details-impressions"
                            class="row-details-impressions"
                          >
                            <div
                              data-test="creative-impressions"
                            >
                              {{ toLocaleString(creative.impressions) }}
                            </div>
                            <div class="small text-muted">
                              Impressions
                            </div>
                          </td>
                          <td
                            ref="row-details-clicks"
                            class="row-details-clicks"
                          >
                            <div
                              data-test="creative-clicks"
                            >
                              {{ toLocaleString(creative.clicks) }}
                            </div>
                            <div class="small text-muted">
                              Clicks
                            </div>
                          </td>
                          <td
                            ref="row-details-ctr"
                            class="row-details-ctr"
                          >
                            <div
                              data-test="creative-ctr"
                            >
                              {{ toLocaleString(creative.ctr, true) }}%
                            </div>
                            <div class="small text-muted">
                              CTR
                            </div>
                          </td>
                          <td
                            v-if="hasScrollsData"
                            ref="row-details-scrolls"
                            class="row-details-scrolls"
                          >
                            <div
                              data-test="creative-clicks"
                            >
                              {{ isValidNumber(creative.scrolls) ? toLocaleString(creative.scrolls) : "N/A" }}
                            </div>
                            <div class="small text-muted">
                              Scrolls
                            </div>
                          </td>
                          <td
                            v-if="hasScrollsData"
                            ref="row-details-str"
                            class="row-details-str"
                          >
                            <div
                              data-test="creative-ctr"
                            >
                              {{ isValidNumber(creative.str) ? toLocaleString(creative.str, true) + '%' : "N/A" }}
                            </div>
                            <div class="small text-muted">
                              STR
                            </div>
                          </td>
                          <td
                            ref="row-details-cost"
                            class="row-details-cost"
                          >
                            <div
                              data-test="creative-cost"
                            >
                              {{ formattedMoneyAmount(creative.cost) }}
                            </div>
                            <div class="small text-muted">
                              Cost
                            </div>
                          </td>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template slot="bottom-row">
                    <td>
                      <b class="pl-3 mb-0">Total</b>
                    </td>
                    <td><b>{{ formatDateForEoC(startDateValue) }} - {{ formatDateForEoC(endDateValue) }}</b></td>
                    <td><b>{{ toLocaleString(Number(impressionsTotal)) }}</b></td>
                    <td><b>{{ toLocaleString(Number(clicksTotal)) }}</b></td>
                    <td><b>{{ toLocaleString(parseFloat(CTRTotal), true) }}%</b></td>
                    <td v-if="hasScrollsData">
                      <b>{{ toLocaleString(Number(scrollsTotal)) }}</b>
                    </td>
                    <td v-if="hasScrollsData">
                      <b>{{ toLocaleString(parseFloat(STRTotal), true) }}%</b>
                    </td>
                    <td><b>{{ formattedMoneyAmount(costTotal) }}*</b></td>
                  </template>
                </b-table>
                <p
                  class="small text-right mt-6"
                  v-if="!isDeliveryTableBusy"
                >
                  * Due to decimal rounding, discrepancies in cost may arise between Daily Delivery and Campaign Overview tab totals.
                </p>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </section>
    </template>
  </div>
</template>

<script>

import {
  mapActions, mapState, mapGetters,
} from 'vuex';

import * as campaignTypes from '@/modules/campaigns/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as contentTypes from '@/modules/content/store/types';

import DeliveryChart from '@/modules/reporting/components/DeliveryChart.vue';
import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import DatePickerDropdown from '@/components/DatePickerDropdown.vue';

import helper from '@/core/helper';
import currenciesMixin from '@/core/mixins/currencies';
import fileHelper from '@/core/mixins/fileHelper';
import numbersHelper from '@/core/mixins/numbersHelper';
import dateHelper from '@/core/mixins/dateHelper';

export default {
  name: 'ReportingDelivery',
  mixins: [
    currenciesMixin,
    fileHelper,
    numbersHelper,
    dateHelper,
  ],
  components: {
    BaseFilterDropdown,
    DatePickerDropdown,
    DeliveryChart,
  },
  data: () => ({
    isPageLoading: true,
    campaignPublishersList: [],
    campaignContentItems: [],
    selectedPublishersIds: [],
    selectedCreativesIds: [],
    selectedContentItemIds: [],
    today: new Date(),
    dateRange: {
      start: -1,
      end: -1,
    },
    dailyStats: [],
    deliveryChartData: {
      loaded: false,
      labels: [],
      impressions: [],
      ctr: [],
      str: [],
      cost: [],
      currencySymbol: '$',
    },
    resizeObserver: null,
  }),
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'currencies',
      'order',
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
      contentTypes.FETCH_CONTENT_ITEMS,
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
    ]),
    returnHeaders() {
      this.assignColumnsWidth();
      this.assignRowDetailsColumnsWidth();
      const headersWithScroll = [
        { key: 'show_details', label: '' },
        'date',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
        'scrolls',
        { key: 'str', label: 'str%' },
        'cost',
      ];
      const headersWithNoScroll = [
        { key: 'show_details', label: '' },
        'date',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
        'cost',
      ];
      return this.hasScrollsData ? headersWithScroll : headersWithNoScroll;
    },
    hasScrollsData() {
      return this.dailyStats.some((delivery) => delivery.scrolls != null);
    },
    hasDeliveryData() {
      return (this.dailyStats.length !== 0);
    },
    minStartDate() {
      return this.order.startDate;
    },
    maxEndDate() {
      return this.order.endDate;
    },
    startDateValue() {
      if (this.dateRange.start !== -1) return this.convertDateToString(new Date(this.dateRange.start));
      return this.minStartDate || this.convertDateToString(this.today);
    },
    endDateValue() {
      if (this.dateRange.end !== -1) return this.convertDateToString(new Date(this.dateRange.end));
      return this.GET_IS_CAMPAIGN_ENDED || new Date(this.today) > new Date(this.maxEndDate) ? this.maxEndDate : this.convertDateToString(this.today);
    },
    impressionsTotal() {
      return this.getEventsAmount('impressions');
    },
    clicksTotal() {
      return this.getEventsAmount('clicks');
    },
    scrollsTotal() {
      return this.getEventsAmount('scrolls');
    },
    costTotal() {
      return this.getEventsAmount('cost');
    },
    scrollableImpressions() {
      return this.getEventsAmount('scrollableImpressions');
    },
    CTRTotal() {
      return this.clicksTotal <= 0 ? 0 : this.getEventRate(this.clicksTotal, this.impressionsTotal);
    },
    STRTotal() {
      return this.scrollsTotal <= 0 ? 0 : this.getEventRate(this.scrollsTotal, this.scrollableImpressions);
    },
    currencySymbol() {
      if (this.dailyStats.length === 0) return '';
      if (!this.currencies) return '$';
      const { code } = this.currencies
        .find((currency) => currency.id === this.order.currencyId);
      return code ? this.currencyMap[code] : '$';
    },
    isDeliveryTableBusy() {
      return !this.dailyStats.length
        && !this.deliveryChartData.loaded;
    },
    allParametersArePresent() {
      return this.selectedPublishersIds.length > 0 && this.selectedCreativesIds.length > 0 && this.selectedContentItemIds.length > 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('reporting', [
      reportingTypes.DOWNLOAD_DAILY_REPORT_CSV,
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_DAILY_STATS,
      reportingTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES_AS_SHAREE,
      contentTypes.FETCH_PUBLISHED_CONTENT_ITEMS,
    ]),
    updatedSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);
      this.updateReport();
    },
    selectAllOptions(fullList, selectedOptionsList, keyProperty) {
      this[selectedOptionsList] = this[fullList].map((option) => option[keyProperty]);
      this.updateReport();
    },
    deselectAllOptions(selectedOptionsList) {
      this[selectedOptionsList] = [];
    },
    async downloadAsCSV() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        campaignId: this.campaign.id,
        body: {
          fromDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
          toDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
          reportType: 'DELIVERY_REPORT',
        },
      };
      const csvContent = await this.DOWNLOAD_DAILY_REPORT_CSV(payload);
      this.downloadCsvFile(csvContent, `${this.campaign.name} - Delivery Report`);
    },
    populateDailyDeliveryChartData(stats) {
      for (let i = 0; i < stats.length; i++) {
        this.deliveryChartData.labels.push(this.formatDate(stats[i].date, 'dd/MM'));
        this.deliveryChartData.impressions.push(stats[i].impressions);
        this.deliveryChartData.ctr.push(stats[i].ctr);

        if (this.hasScrollsData) {
          this.deliveryChartData.str.push(stats[i].str);
        }
        this.deliveryChartData.cost.push(stats[i].cost);
      }
      this.deliveryChartData.currencySymbol = this.currencySymbol;
    },
    fetchPublisherData() {
      this.dailyStats = [];
      this.resetDeliveryChart();
      const payload = {
        advertiserId: this.campaign.advertiserId,
        campaignId: this.campaign.id,
        body: {
          fromDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
          toDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };
      this.FETCH_CAMPAIGN_PUBLISHER_DAILY_STATS(payload)
        .then((response) => {
          this.dailyStats = response.dailyStats;
          this.populateDailyDeliveryChartData(response.dailyStats);
          this.deliveryChartData.loaded = true;
        });
    },
    fetchPublishers() {
      return this.FETCH_PUBLISHERS();
    },
    fetchPublisherIdsWithStats() {
      const payload = {
        creativeIds: this.selectedCreativesIds.join(','),
      };
      return this.FETCH_PUBLISHER_IDS_WITH_STATS(payload);
    },
    fetchContentItems() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        feedId: this.feedId,
      };
      return this.FETCH_PUBLISHED_CONTENT_ITEMS(payload);
    },
    createPublisherList(fullPublishersList, publishersIdsWithStats) {
      this.campaignPublishersList = fullPublishersList.flat()
        .filter((publisher) => publishersIdsWithStats.includes(publisher.id));
    },
    createCampaignItems(contentItems) {
      this.campaignContentItems = contentItems;
    },
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    updateReport: helper.debounce(function fetchData() {
      if (this.selectedPublishersIds.length > 0 && this.selectedCreativesIds.length > 0 && this.selectedContentItemIds.length > 0) {
        this.fetchPublisherData();
      } else {
        this.deliveryChartData.loaded = true;
      }
    }, 1500),
    resetDeliveryChart() {
      this.deliveryChartData = {
        loaded: false,
        labels: [],
        impressions: [],
        ctr: [],
        str: [],
        cost: [],
      };
    },
    assignColumnsWidth() {
      const rootElement = document.querySelector(':root');
      const width = this.hasScrollsData ? '8%' : '10%';
      rootElement.style.setProperty('--not-first-or-second-column-width', width);
    },
    async assignRowDetailsColumnsWidth() {
      await this.$nextTick();
      if (Object.keys(this.$refs).length) {
        const rootElement = document.querySelector(':root');
        rootElement.style.setProperty('--row-details-icon-width', `${this.$refs['icon-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-size-name-width', `${this.$refs['date-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-impressions-width', `${this.$refs['impressions-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-clicks-width', `${this.$refs['clicks-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-ctr-width', `${this.$refs['ctr-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-scrolls-width', `${this.$refs['scrolls-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-str-width', `${this.$refs['str-column']?.clientWidth}px`);
        rootElement.style.setProperty('--row-details-cost-width', `${this.$refs['cost-column']?.clientWidth}px`);
      }
    },
    async attachResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.assignRowDetailsColumnsWidth();
      });
      await this.$nextTick();
      this.resizeObserver.observe(this.$refs['delivery-report-table']?.$el);
    },
    async handleCreate() {
      try {
        this.selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id');
        const fetchPublishers = this.fetchPublishers();
        const fetchPublisherIdsWithStats = this.fetchPublisherIdsWithStats();
        const fetchContentItems = this.fetchContentItems();
        Promise.all([fetchPublishers, fetchPublisherIdsWithStats, fetchContentItems])
          .then(([fullPublishersList, publisherIdsWithStats, contentItems]) => {
            this.createPublisherList(fullPublishersList, publisherIdsWithStats);
            this.createCampaignItems(contentItems);
            this.selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id');
            this.selectAllOptions('campaignContentItems', 'selectedContentItemIds', 'encodedExternalId');
            this.isPageLoading = false;
            this.attachResizeObserver();
          });
      } catch (e) {
        this.isPageLoading = false;
        this.$router.back();
      }
    },
    hasCreativeStats(item) {
      return (!!item.creativeStats);
    },
    getEventsAmount(name) {
      if (this.dailyStats.length === 0) return '-';
      const { dailyStats } = this;
      return Object.keys(dailyStats)
        .map((key) => dailyStats[key][name])
        .filter((eventsAmount) => eventsAmount)
        .reduce((amount1, amount2) => amount1 + amount2, 0);
    },
    getEventRate(eventsAmount, impressionsAmount) {
      if (this.dailyStats.length === 0) return '-';
      if (!eventsAmount || !impressionsAmount) return '-';
      return ((eventsAmount) / (impressionsAmount) * 100);
    },
    formattedMoneyAmount(budget) {
      return this.getFormattedMoneyAmountByOrder(Number(budget).toFixed(2), this.order);
    },
  },
  async created() {
    const { campaignId } = this.$route.params;
    this.campaignId = campaignId;
    await this.FETCH_EXISTING_CREATIVES_AS_SHAREE(campaignId);
    this.handleCreate();
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs['delivery-report-table']?.$el);
  },
};
</script>
<style scoped lang="scss">
$not-first-or-second-column-width: 10%;
::v-deep {
  .card-table thead tr th:first-child,
  .card-table tbody tr td:first-child {
    min-width: unset;
    width: 50px;
    padding: 0px;
    margin: 0px;
  }
  .card-table thead tr th:nth-child(n+3),
  .card-table tbody tr td:nth-child(n+3) {
    width: var(--not-first-or-second-column-width, $not-first-or-second-column-width);
  }
  .card-table thead tr th:not(:last-child),
  .card-table tbody tr td:not(:last-child) {
    border-right: 1px solid rgb(222, 226, 230);
  }

  .card-table .b-table-details {
    display: table-row;
  }
  .card-table .b-table-details td .custom-row-details-wrapper {
    display: block;
    width: 100%;
    overflow: hidden;
  }

  .card-table .b-table-details td .custom-row-details-wrapper > div > div {
    display: inline-block;
    flex-grow: 0;
    flex-shrink: 0;
  }

  .row-details-icon {
    // !important is needed because of specificity problems.
    width: var(--row-details-icon-width, '50px') !important;
  }
  .row-details-size-name {
    // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
    width: calc(var(--row-details-size-name-width) + calc(0.75rem * 2));
  }
  .row-details-impressions {
    // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
    // !important is needed because of specificity problems.
    width: calc(var(--row-details-impressions-width) + calc(0.75rem * 2)) !important;
  }
  .row-details-clicks {
    width: var(--row-details-clicks-width);
  }
  .row-details-ctr {
    width: var(--row-details-ctr-width);
  }
  .row-details-scrolls {
    width: var(--row-details-scrolls-width);
  }
  .row-details-str {
    width: var(--row-details-str-width);
  }
  .row-details-cost {
    width: var(--row-details-cost-width);
  }
  .b-table-bottom-row {
    border-top: 2px solid #2F404E;
  }
}
</style>
